import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  HeartIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const stats = [
    { label: 'Americans die each day waiting for an organ', value: '18', color: 'text-red-600' },
    { label: 'of recovered kidneys went unused in 2024', value: '22%', color: 'text-amber-600' },
    { label: 'rise in kidney non-utilization over five years', value: '83%', color: 'text-orange-600' },
    { label: 'projected reduction in non-utilization', value: '~40%', color: 'text-green-600' },
  ];

  const features = [
    {
      icon: <ChartBarIcon className="h-6 w-6" />,
      title: 'Predict Non-Utilization',
      description: 'Flag organs likely to go unused before they cycle through the waitlist.',
      color: 'bg-blue-600',
      items: ['National 100-offer acceptance model', 'Acts before the 12-hour window', 'High-precision predictions']
    },
    {
      icon: <ComputerDesktopIcon className="h-6 w-6" />,
      title: 'Find the Right Region',
      description: 'Identify the transplant regions most likely to accept an organ.',
      color: 'bg-purple-600',
      items: ['Regional acceptance models', 'Calibrated risk estimates']
    },
    {
      icon: <ShieldCheckIcon className="h-6 w-6" />,
      title: 'Explain & Defer',
      description: 'Every recommendation comes with explanations — and defers to clinicians when uncertain.',
      color: 'bg-emerald-600',
      items: ['SHAP feature analysis', '"What-if" scenario probes', 'Knows when not to predict']
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Upload Case Data',
      description: 'Securely upload data from DonorNet, or a CSV file.'
    },
    {
      number: '02',
      title: 'Get Predictions',
      description: 'Receive instant AI-powered predictions and analysis'
    },
    {
      number: '03',
      title: 'Explore Insights',
      description: 'Explore results with AI-assisted explanations and test what-if scenarios'
    },
    {
      number: '04',
      title: 'Give Feedback',
      description: 'Share feedback through the chat to continually improve the models'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3"
            >
              <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                <HeartIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-semibold text-xl text-gray-900">AllocIQ</span>
                <span className="block text-xs text-gray-500">Human-AI Organ Allocation</span>
              </div>
            </motion.div>

            {/* Auth-aware actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/about')}
                className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
              >
                About
              </button>
              {user ? (
                <>
                  <span className="hidden sm:flex items-center gap-2 text-sm text-gray-600">
                    <span className="h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                      {user.username?.[0]?.toUpperCase() || 'U'}
                    </span>
                    <span className="font-medium text-gray-800">{user.username}</span>
                  </span>
                  <button
                    onClick={() => navigate('/experimental')}
                    className="hidden sm:inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium border border-gray-200 transition-all"
                  >
                    <BeakerIcon className="h-4 w-4" />
                    Explorer
                  </button>
                  <button
                    onClick={() => navigate('/chat')}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-lg shadow-blue-200 transition-all"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                    Chat
                  </button>
                  <button
                    onClick={() => logout()}
                    className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-lg shadow-blue-200 transition-all"
                >
                  Sign In
                  <ArrowRightIcon className="h-4 w-4" />
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" />
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] -z-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-8"
            >
              <AcademicCapIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">An MIT × Mass General Brigham collaboration</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Every day, 18 Americans die waiting for an organ —
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                while usable kidneys go to waste.
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              We're changing that through human-AI collaborative model development. For the first time ever, we are providing clinicians direct access to our AI models, with an LLM-powered chat interface, where they can query our latest models to understand which organs should be re-routed off-sequence. They can also provide their feedback through the LLM-chat, which is then used to continually improve the models.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(user ? '/chat' : '/login')}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-medium shadow-xl shadow-blue-200 transition-all"
              >
                {user ? 'Go to Chat' : 'Begin'}
                <ArrowRightIcon className="h-5 w-5" />
              </motion.button>
              {user && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/experimental')}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-8 py-4 rounded-xl font-medium border border-gray-200 shadow-lg transition-all"
                >
                  <BeakerIcon className="h-5 w-5" />
                  Open Explorer
                </motion.button>
              )}
              {/* Watch Demo — hidden until the demo video is ready
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-8 py-4 rounded-xl font-medium border border-gray-200 shadow-lg transition-all"
              >
                Watch Demo
              </motion.button>
              */}
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-16"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -4 }}
                className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-xl shadow-gray-200/20 text-center"
              >
                <div className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="bg-white border-y border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-grid-white/[0.1] [mask-image:linear-gradient(0deg,transparent,black)]" />

            <div className="relative max-w-3xl mx-auto text-center">
              <p className="text-lg md:text-xl leading-relaxed opacity-90">
                In 2024, more than <span className="font-bold">45,000</span> kidneys were recovered in the U.S. —
                yet over <span className="font-bold">9,000</span> were declined and never used, a{' '}
                <span className="font-bold">22%</span> non-utilization rate that has climbed{' '}
                <span className="font-bold">83%</span> in five years. When an organ is repeatedly declined, it can be
                placed out of sequence and offered to a region where a patient is a strong match. Predicting these
                cases early is a major opportunity to save lives.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50/50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What the system does
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Reliable, multi-criteria predictions that keep clinicians in the loop — not a black box.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                className="bg-white rounded-2xl p-6 border border-gray-200/50 shadow-xl shadow-gray-200/20"
              >
                <div className={`h-12 w-12 ${feature.color} rounded-xl flex items-center justify-center text-white mb-4 shadow-lg`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 mb-4 text-sm">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <ChevronRightIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Quote */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 text-6xl text-blue-200 font-serif">"</div>
            <div className="absolute -bottom-4 -right-4 text-6xl text-blue-200 font-serif">"</div>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 border border-gray-200/50 shadow-xl">
              <p className="text-xl md:text-2xl text-gray-800 leading-relaxed text-center italic">
                With AI intervention, we can predict whether organs will be accepted within 100 offers before they go through waitlist
                allocation and identify which region is most likely to accept an organ. This enables proactive
                strategies to reduce organ non-utilization and guides the design of out-of-sequence allocation policies.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50/50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              A simple four-step process to get started
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-blue-200 to-purple-200" />
                )}
                <div className="relative bg-white rounded-2xl p-6 border border-gray-200/50 shadow-xl shadow-gray-200/20 text-center">
                  <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold mx-auto mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Help reduce organ waste.
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Explore the models and see where AI can support better, faster allocation decisions.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(user ? '/chat' : '/login')}
              className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 rounded-xl font-medium shadow-xl shadow-blue-900/20 transition-all"
            >
              {user ? 'Go to Chat' : 'Get Started Now'}
              <ArrowRightIcon className="h-5 w-5" />
            </motion.button>
            <p className="text-blue-200 mt-4 text-sm">
              {user
                ? <>Signed in as <span className="font-medium text-white">{user.username}</span></>
                : <>Already have an account? <button onClick={() => navigate('/login')} className="underline hover:text-white">Sign in</button></>}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200/50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <HeartIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-gray-600">© 2026 AllocIQ. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Terms of Service</a>
              <button onClick={() => navigate('/about')} className="text-sm text-gray-600 hover:text-blue-600 transition-colors">About</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
